import React, { ReactNode } from 'react';
import HBField, { Props } from '../HBField/HBField';
import { QuestionMarkCircle16 } from '@honeybook/icons';
import { vars } from '../../theme';
import styled from 'styled-components';
import HBTooltip from '../HBTooltip/HBTooltip';
import type * as CSS from 'csstype';
import { MustIncludeOneOfBasicTestIdWithIdProps } from '../common/internal/test-id.types';

type FieldProps = Props & {
    /** @deprecated Enable DS Next styling mode. Will be removed post-migration; prefer omitting. */
    isDSNext?: true;
    /** Content rendered inside the help tooltip that appears next to the label. */
    helpTooltipContent: ReactNode;
    /** Callback fired when the help tooltip opens, useful for analytics tracking. */
    onTooltipOpen?: () => void;
    /** Custom icon element replacing the default question-mark circle icon next to the label. */
    infoIcon?: JSX.Element;
};

interface ILabelWithInfoProps {
    helpTooltipContent: ReactNode;
    label: ReactNode;
    onTooltipOpen?: () => void;
    icon?: JSX.Element;
}

const labelIconStylesObject = {
    width: '2em',
    textAlign: 'center' as CSS.Property.TextAlign,
    paddingTop: 2
};

const LabelWithInfo = ({
    label,
    icon,
    helpTooltipContent,
    onTooltipOpen
}: ILabelWithInfoProps) => {
    const onOpenChange = ({ isOpen }: { isOpen: boolean }) => {
        if (isOpen) {
            onTooltipOpen();
        }
    };

    const Tooltip = (
        <WrappedTooltip
            onOpenChange={onTooltipOpen && onOpenChange}
            offsetY={6}
            maxWidth="250px"
            placement="top"
            targetContainerStyle={labelIconStylesObject}
            target={
                icon || (
                    <QuestionMarkCircle16
                        color={vars.colors.text.tertiary}
                        hoverColor={vars.colors.text.secondary}
                    />
                )
            }
        >
            {helpTooltipContent}
        </WrappedTooltip>
    );

    return (
        <>
            {label}
            {Tooltip}
        </>
    );
};

const HBFieldWithInfo = ({
    helpTooltipContent,
    label,
    onTooltipOpen,
    infoIcon,
    ...otherProps
}: FieldProps & MustIncludeOneOfBasicTestIdWithIdProps) => {
    const labelWithInfoProps = {
        helpTooltipContent,
        label,
        onTooltipOpen,
        icon: infoIcon
    };

    return (
        <HBField
            withIcon
            label={<LabelWithInfo {...labelWithInfoProps} />}
            {...otherProps}
        />
    );
};

export default HBFieldWithInfo;

const WrappedTooltip = styled(HBTooltip)`
    white-space: pre-line;
`;
